import { Category } from '@/apis/providers/types/provider.types';
import { NewReimbursementLayout } from '@/layouts/reimbursements/new_reimbursements/NewReimbursementLayout';
import { SelectedPurse } from '@/storage/applications/providerPaySlice';
import { MutationStatus } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { AddReimbursementDetails } from './add_store_details.page';
import ReimbursementsAllocateCategoriesPage from './allocate_categories.page';
import { ReimbursementApplicationSent } from './application_sent.page';
import ReimbursementReviewPage from './review/review.page';
import ReimbursementSelectCategoriesPage from './select_categories.page';
import { ReimbursementSelectPursesPage } from './select_purses/select_purses.page';
import { NewReimubrusementCaptureImages } from './upload_images.page';
import { ReimbursementStep } from './reimbursements.constants';
import { selectProgramSettings } from '@/storage/program_settings/programSettingsSlice';
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

// Define the possible steps in the reimbursement flow
// Convert from enum to object constant
export type PaymentProps<T> = {
  onNext: (data: T) => void;

}


export type ReimbursementDetails = {
  store_name: string;
  amount: number;
  documents: string[];
  purses: SelectedPurse[]
  invoiceNumber: string;
  comments: string;
  expenseCategories: Category[]
}

type Props = {
  defaultValues?: ReimbursementDetails
  onSubmit: (paylod: ReimbursementDetails) => void
  mutationStatus: MutationStatus
  startStep?: ReimbursementStep
}


// Component to handle the reimbursement flow
const CreateReimbursementForm = (props: Props) => {
  const { defaultValues, onSubmit, mutationStatus, startStep } = props
  const [currentStep, setCurrentStep] = useState<ReimbursementStep>(startStep ?? ReimbursementStep.ADD_DETAILS);
  const [reimbursementData, setReimbursementData] = useState<ReimbursementDetails>(() => {

    if (defaultValues) {
      return {
        amount: defaultValues.amount,
        store_name: defaultValues.store_name,
        documents: defaultValues.documents,
        purses: defaultValues.purses,
        invoiceNumber: defaultValues.invoiceNumber,
        comments: defaultValues.comments,
        expenseCategories: defaultValues.expenseCategories
      }
    }

    return {
      store_name: '',
      amount: 0,
      documents: [],
      purses: [],
      invoiceNumber: '',
      expenseCategories: [],
      comments: '',
    }
  });
  const navigate = useNavigate()
  const programSettings = useSelector(selectProgramSettings)

  useEffect(() => {
    if (programSettings.reimbursement_module == 0) 
      navigate(-1)
  },[navigate]);

  // Function to move to the next step
  const goToNextStep = (nextStep: ReimbursementStep) => {
    setCurrentStep(nextStep);
  };

  function updateReimbursementData(data: Partial<ReimbursementDetails>) {
    setReimbursementData((prevData) => ({ ...prevData, ...data }));
  }

  // Function to go back to the previous step (if needed)
  const goToPreviousStep = () => {
    switch (currentStep) {
      case ReimbursementStep.CAPTURE_IMAGES:
        setCurrentStep(ReimbursementStep.ADD_DETAILS);
        break;
      case ReimbursementStep.CHOOSE_PURSE:
        setCurrentStep(ReimbursementStep.CAPTURE_IMAGES);
        break;
      case ReimbursementStep.SELECT_CATEGORIES:
        setCurrentStep(ReimbursementStep.CHOOSE_PURSE);
        break;
      case ReimbursementStep.REVIEW:
        setCurrentStep(ReimbursementStep.ALLOCATE_CATEGORIES);
        break;
      case ReimbursementStep.ALLOCATE_CATEGORIES:
        setCurrentStep(ReimbursementStep.SELECT_CATEGORIES);
        break;
      case ReimbursementStep.COMPLETE:
        break;
      default:
        break;
    }
  };

  // Render the appropriate component based on the current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case ReimbursementStep.ADD_DETAILS:
        return (
          <AddReimbursementDetails
            defaultValues={{
              amount: reimbursementData.amount,
              store_name: reimbursementData.store_name,
            }}
            onSubmitSuccess={(data) => {
              updateReimbursementData({
                store_name: data.store_name,
                amount: data.amount,
              })
              goToNextStep(ReimbursementStep.CAPTURE_IMAGES)
            }}
          />
        );
      case ReimbursementStep.CAPTURE_IMAGES:
        return (
          <NewReimubrusementCaptureImages
            onUploadedDocumentRemove={(index) => {
              updateReimbursementData({
                documents: reimbursementData.documents.filter((_, i) => i !== index)
              })
            }}
            defaultValues={reimbursementData.documents}
            onNext={(data) => {
              updateReimbursementData({
                documents: [...reimbursementData.documents, ...data]
              })
              goToNextStep(ReimbursementStep.CHOOSE_PURSE)
            }}
          />
        );
      case ReimbursementStep.CHOOSE_PURSE:
        return (
          <ReimbursementSelectPursesPage
            amount={reimbursementData.amount}
            defaultValues={reimbursementData.purses}
            onNext={(purses) => {

              updateReimbursementData({
                purses
              })
              goToNextStep(ReimbursementStep.SELECT_CATEGORIES)
            }}
          />
        );
      case ReimbursementStep.SELECT_CATEGORIES:
        return (
          <ReimbursementSelectCategoriesPage
            defaultValues={{
              comments: reimbursementData.comments,
              expenseCategories: reimbursementData.expenseCategories,
              invoiceNumber: reimbursementData.invoiceNumber,
            }}
            onNext={(data) => {
              updateReimbursementData({
                expenseCategories: data.expenseCategories,
                comments: data.comments,
                invoiceNumber: data.invoiceNumber,
              })
              goToNextStep(ReimbursementStep.ALLOCATE_CATEGORIES)
            }}
          />
        );
      case ReimbursementStep.REVIEW:
        return (
          <ReimbursementReviewPage
            mutationStatus={mutationStatus}
            onSubmit={() => {
              onSubmit(reimbursementData)
            }}
            paymentDetails={reimbursementData}
          />
        );
      case ReimbursementStep.ALLOCATE_CATEGORIES:
        return (
          <ReimbursementsAllocateCategoriesPage
            amount={reimbursementData.amount}
            defaultValues={reimbursementData.expenseCategories}
            onNext={(data) => {
              console.log(data, 'here')
              updateReimbursementData({
                expenseCategories: data
              })
              goToNextStep(ReimbursementStep.REVIEW)
            }}
          />
        );
      case ReimbursementStep.COMPLETE:
        return (
          <ReimbursementApplicationSent
          />
        );
      default:
        <AddReimbursementDetails
          defaultValues={{
            amount: reimbursementData.amount,
            store_name: reimbursementData.store_name,
          }}
          onSubmitSuccess={() => goToNextStep(ReimbursementStep.CAPTURE_IMAGES)}
        />
    }
  };

  return (
    <NewReimbursementLayout currentStep={currentStep} onBack={goToPreviousStep}>

      <div className="reimbursement-stepper">
        {renderCurrentStep()}
      </div>
    </NewReimbursementLayout>
  );
};

export default CreateReimbursementForm;